import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {WorkOrderService} from '../../../Services/work-order.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {TransactionEntityService} from '../../../Services/transaction-entity.service';
import {InvoiceDocumentService} from '../../../Services/invoice-document.service';
import {WoInvoicingService} from '../../../Services/wo-invoicing.service';
import {Modal} from 'bootstrap';
import {
  WoBillingSummary,
  WoHoursBillingLine,
  WoTravelBillingLine,
  WoMaterialBillingLine,
  WoInvoiceLine,
  WoLinkedInvoice
} from '../../../DataModels/workorder-model';

@Component({
  selector: 'app-work-orders-billing',
  templateUrl: './work-orders-billing.component.html',
  standalone: false,
})
export class WorkOrdersBillingComponent implements OnChanges {

  @ViewChild('woEmailModal', {read: ElementRef}) modalElementRef!: ElementRef;

  @Input() workorderId: string | null = null;
  @Input() entityId: string | null = null;
  @Input() customerEmail: string = '';
  @Input() woNumber: string = '';
  @Input() billingStatus: string = 'unbilled';
  @Input() invoiceId: string | null = null;
  @Input() estimatedCost: number | null = null;
  @Output() invoiceDeleted = new EventEmitter<string>(); // emits new billing_status
  @Output() invoiceCreated = new EventEmitter<void>();

  summary: WoBillingSummary | null = null;
  linkedInvoices: WoLinkedInvoice[] = [];
  deletingInvoiceId: string | null = null;

  // typed line arrays for template
  get hoursLines(): WoHoursBillingLine[] {
    return this.summary?.hours ?? [];
  }

  get travelLines(): WoTravelBillingLine[] {
    return this.summary?.travel ?? [];
  }

  get materialLines(): WoMaterialBillingLine[] {
    return this.summary?.materials ?? [];
  }
  isLoaded = false;
  generating = false;

  confirmForm = {group_by_type: true};

  // Entity defaults
  entityHoursItemId: string | null = null;
  entityTravelItemId: string | null = null;

  // Track which lines are selected for invoicing
  selectedHours: Set<string> = new Set();
  selectedTravel: Set<string> = new Set();
  selectedMaterials: Set<string> = new Set();

  reportDataToSendInEmail: any = null;

  constructor(
    private woService: WorkOrderService,
    private notification: BsToastService,
    public confirmDialog: ConfirmDialogService,
    private entityService: TransactionEntityService,
    private invoiceDocService: InvoiceDocumentService,
    private invoicingService: WoInvoicingService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workorderId'] && this.workorderId) {
      this.load();
    }
  }

  load(): void {
    this.isLoaded = false;
    this.woService.getLinkedInvoices(this.workorderId!).subscribe({
      next: (invoices) => {
        this.linkedInvoices = invoices ?? [];
      },
      error: () => {
        this.linkedInvoices = [];
      }
    });
    this.woService.getBillingSummary(this.workorderId!).subscribe({
      next: (res) => {
        this.summary = res;
        this.selectedHours = new Set(res.hours.filter(l => l.billable && !l.invoiced).map(l => l.id));
        this.selectedTravel = new Set(res.travel.filter(l => l.billable && !l.invoiced).map(l => l.id));
        this.selectedMaterials = new Set(res.materials.filter(l => l.billable && !l.invoiced).map(l => l.id));
        this.isLoaded = true;
      },
      error: () => {
        this.notification.showErrorToast('Error loading billing summary.');
        this.isLoaded = true;
      }
    });
  }

  toggle(set: Set<string>, id: string): void {
    if (set.has(id)) {
      set.delete(id);
    } else {
      set.add(id);
    }
  }

  get selectedTotal(): number {
    if (!this.summary) {
      return 0;
    }
    const h = this.summary.hours.filter(l => this.selectedHours.has(l.id)).reduce((s, l) => s + (l.billed_amount ?? (l.hours * l.billing_rate)), 0);
    const t = this.summary.travel.filter(l => this.selectedTravel.has(l.id)).reduce((s, l) => s + (l.billed_amount ?? (l.units * l.rate_per_unit)), 0);
    const m = this.summary.materials.filter(l => this.selectedMaterials.has(l.id)).reduce((s, l) => s + (l.billed_amount ?? (l.quantity * l.unit_price)), 0);
    return Math.round((h + t + m) * 100) / 100;
  }

  get hoursTotal(): number {
    return this.summary?.hours.reduce((s, l) => s + (l.billed_amount ?? (l.hours * l.billing_rate)), 0) ?? 0;
  }

  get travelTotal(): number {
    return this.summary?.travel.reduce((s, l) => s + (l.billed_amount ?? (l.units * l.rate_per_unit)), 0) ?? 0;
  }

  get materialsTotal(): number {
    return this.summary?.materials.reduce((s, l) => s + (l.billed_amount ?? (l.quantity * l.unit_price)), 0) ?? 0;
  }

  get estimateVariance(): number | null {
    if (this.estimatedCost == null || this.estimatedCost === 0) {
      return null;
    }
    return this.selectedTotal - this.estimatedCost;
  }

  get estimateBadgeClass(): string {
    const v = this.estimateVariance;
    if (v == null) {
      return '';
    }
    return v > 0 ? 'bg-warning text-dark' : 'bg-success';
  }

  get hasSelection(): boolean {
    return this.selectedHours.size > 0 || this.selectedTravel.size > 0 || this.selectedMaterials.size > 0;
  }

  buildLines(): WoInvoiceLine[] {
    const lines: WoInvoiceLine[] = [];
    if (this.summary) {
      this.summary.hours.filter(l => this.selectedHours.has(l.id)).forEach(l => {
        lines.push({id: l.id, item_id: this.entityHoursItemId ?? ''});
      });
      this.summary.travel.filter(l => this.selectedTravel.has(l.id)).forEach(l => {
        lines.push({id: l.id, item_id: this.entityTravelItemId ?? ''});
      });
      this.summary.materials.filter(l => this.selectedMaterials.has(l.id)).forEach(l => {
        lines.push({id: l.id, item_id: l.item_id ?? ''});
      });
    }
    return lines;
  }

  confirmAndCreateInvoice(): void {
    // Load entity defaults for item_id resolution
    if (this.entityId) {
      this.entityService.loadEntityContext(this.entityId).subscribe(ctx => {
        if (ctx?.entity) {
          this.entityHoursItemId = (ctx.entity as any).workorder_hours_item_id ?? null;
          this.entityTravelItemId = (ctx.entity as any).workorder_travel_item_id ?? null;
        }
      });
    }
    const currencyFmt = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});
    const amount = currencyFmt.format(this.selectedTotal);
    const messages = [
      `Hours: ${currencyFmt.format(this.hoursTotal)} | Travel: ${currencyFmt.format(this.travelTotal)} | Materials: ${currencyFmt.format(this.materialsTotal)}`,
    ];
    if (this.estimatedCost) {
      const variance = this.estimateVariance ?? 0;
      messages.push(`Est: ${currencyFmt.format(this.estimatedCost)} | Selected: ${amount} | ${variance >= 0 ? '+' : ''}${currencyFmt.format(variance)} vs estimate`);
    }
    messages.push(`Create invoice for ${amount}?`);
    this.confirmDialog.confirmThis(
      messages,
      () => {
        this.submitInvoice();
      },
      () => {
      }
    );
  }

  async submitInvoice(): Promise<void> {
    if (!this.workorderId) {
      return;
    }
    const lines = this.buildLines();
    this.generating = true;
    const result = await this.invoicingService.invoiceWorkOrder({
      workorderId: this.workorderId,
      customerEmail: this.customerEmail,
      woNumber: this.woNumber,
      lines,
      groupByType: this.confirmForm.group_by_type,
    });
    this.generating = false;
    if (result.success) {
      this.notification.showSuccessToast('Invoice created successfully.');
      this.invoiceCreated.emit();
    } else {
      this.notification.showErrorToast(result.error ?? 'Error generating invoice.');
    }
  }

  private openEmailModal(): void {
    if (this.modalElementRef) {
      const modalDiv = this.modalElementRef.nativeElement.querySelector('.modal');
      if (modalDiv) {
        Modal.getOrCreateInstance(modalDiv).show();
      }
    }
  }

  deleteInvoice(invoice: WoLinkedInvoice): void {
    if (!this.workorderId || !this.invoiceId) {
      return;
    }
    this.confirmDialog.confirmThis(
      [`Delete invoice ${invoice.invoice_number}?`, 'This cannot be undone.'],
      () => {
        this.deletingInvoiceId = this.invoiceId!;
        this.woService.deleteInvoice(this.workorderId!, this.invoiceId!).subscribe({
          next: (res) => {
            this.deletingInvoiceId = null;
            this.notification.showSuccessToast(`Invoice ${res.invoice_number} deleted.`);
            this.invoiceDeleted.emit(res.billing_status);
            this.load();
          },
          error: (err) => {
            this.deletingInvoiceId = null;
            const msg = err?.error?.error ?? err?.error?.message ?? 'Error deleting invoice.';
            this.notification.showErrorToast(msg);
          }
        });
      },
      () => {
      }
    );
  }

  resendInvoice(line: { invoice_detail_id?: string | null }): void {
    if (!line.invoice_detail_id) {
      return;
    }
    this.invoiceDocService.buildEmailModelById(line.invoice_detail_id, 'ar').then(emailModel => {
      emailModel.toEmail = this.customerEmail ?? '';
      this.reportDataToSendInEmail = {...emailModel};
      this.openEmailModal();
    }).catch(() => {
      this.notification.showErrorToast('Error fetching invoice for re-send.');
    });
  }
}
